import React, { useState, useCallback } from 'react';

const styles = {
  base: {
    position: 'relative',
    overflow: 'hidden',
    display: 'inline-block',
    fontFamily: "'DM Sans', sans-serif",
    fontWeight: 600,
    fontSize: '0.95rem',
    borderRadius: '8px',
    padding: '14px 30px',
    cursor: 'pointer',
    border: 'none',
    textDecoration: 'none',
    transition: 'background 0.25s, transform 0.15s, opacity 0.2s, box-shadow 0.25s',
  },
  primary: {
    background: 'var(--orange)',
    color: '#fff',
    boxShadow: '0 4px 14px rgba(245,140,30,0.35)',
  },
  outline: {
    background: 'transparent',
    color: '#fff',
    border: '1.5px solid rgba(255,255,255,0.4)',
    boxShadow: 'none',
  },
  dark: {
    background: 'var(--navy)',
    color: '#fff',
    boxShadow: '0 4px 14px rgba(15,35,64,0.3)',
  },
};

export default function Button({ children, variant = 'primary', href, onClick, type = 'button', style = {}, disabled }) {
  const [pressed, setPressed] = useState(false);
  const [hovered, setHovered] = useState(false);

  const addRipple = useCallback((e) => {
    const btn = e.currentTarget;
    const rect = btn.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height) * 1.8;
    const x = e.clientX - rect.left - size / 2;
    const y = e.clientY - rect.top - size / 2;
    const ripple = document.createElement('span');
    Object.assign(ripple.style, {
      position: 'absolute', borderRadius: '50%',
      width: size + 'px', height: size + 'px',
      left: x + 'px', top: y + 'px',
      background: 'rgba(255,255,255,0.22)',
      transform: 'scale(0)', pointerEvents: 'none',
      animation: 'ripple 0.55s ease-out forwards',
    });
    btn.appendChild(ripple);
    ripple.addEventListener('animationend', () => ripple.remove());
    if (onClick) onClick(e);
  }, [onClick]);

  const computedStyle = {
    ...styles.base,
    ...styles[variant],
    opacity: disabled ? 0.5 : pressed ? 0.72 : hovered ? 0.94 : 1,
    transform: pressed ? 'scale(0.97)' : hovered ? 'translateY(-2px)' : 'none',
    boxShadow: pressed
      ? '0 2px 6px rgba(245,140,30,0.15)'
      : hovered && variant === 'primary'
      ? '0 8px 22px rgba(245,140,30,0.45)'
      : styles[variant].boxShadow,
    background: hovered && variant === 'primary'
      ? 'var(--orange-dark)'
      : hovered && variant === 'outline'
      ? 'rgba(255,255,255,0.1)'
      : styles[variant].background,
    borderColor: hovered && variant === 'outline' ? 'rgba(255,255,255,0.9)' : undefined,
    ...style,
  };

  const events = {
    onMouseEnter: () => setHovered(true),
    onMouseLeave: () => { setHovered(false); setPressed(false); },
    onMouseDown: () => setPressed(true),
    onMouseUp: () => setPressed(false),
    onClick: addRipple,
  };

  if (href) {
    return <a href={href} style={computedStyle} {...events}>{children}</a>;
  }
  return (
    <button type={type} style={computedStyle} disabled={disabled} {...events}>
      {children}
    </button>
  );
}
